import React, { useState, useEffect } from 'react';
import { X, DownloadSimple, DeviceMobile, AppleLogo, AndroidLogo, Share, Plus, Globe } from '@phosphor-icons/react';

const PWAInstallModal = ({ isOpen, onClose }) => {
  const [userAgent, setUserAgent] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const ua = navigator.userAgent;
      setUserAgent(ua);
      setIsIOS(/iPad|iPhone|iPod/.test(ua));
      setIsAndroid(/Android/.test(ua));
      setIsSafari(/Safari/.test(ua) && !/Chrome/.test(ua));
    }
  }, [isOpen]);

  const getInstallInstructions = () => {
    if (isIOS) {
      if (isSafari) {
        return {
          title: 'نصب در آیفون (Safari)',
          steps: [
            { icon: Share, text: 'روی دکمه Share در پایین صفحه ضربه بزنید' },
            { icon: Plus, text: 'گزینه "Add to Home Screen" را انتخاب کنید' },
            { icon: DownloadSimple, text: 'روی "Add" ضربه بزنید تا نصب شود' }
          ]
        };
      } else {
        return {
          title: 'نصب در آیفون',
          steps: [
            { icon: Globe, text: 'ابتدا Safari را باز کنید' },
            { icon: Share, text: 'روی دکمه Share در پایین صفحه ضربه بزنید' },
            { icon: Plus, text: 'گزینه "Add to Home Screen" را انتخاب کنید' }
          ]
        };
      }
    } else if (isAndroid) {
      return {
        title: 'نصب در اندروید',
        steps: [
          { icon: DeviceMobile, text: 'روی منوی سه نقطه در مرورگر ضربه بزنید' },
          { icon: DownloadSimple, text: 'گزینه "Add to Home screen" را انتخاب کنید' },
          { icon: Plus, text: 'روی "Add" ضربه بزنید تا نصب شود' }
        ]
      };
    } else {
      return {
        title: 'نصب در مرورگر',
        steps: [
          { icon: DownloadSimple, text: 'روی آیکون نصب در نوار آدرس کلیک کنید' },
          { icon: Plus, text: 'گزینه "Install" را انتخاب کنید' },
          { icon: DeviceMobile, text: 'اپ در دسکتاپ شما نصب خواهد شد' }
        ]
      };
    }
  };

  const instructions = getInstallInstructions();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <DownloadSimple size={24} weight="bold" />
              </div>
              <div>
                <h2 className="text-xl font-bold">نصب اپلیکیشن</h2>
                <p className="text-purple-100 text-sm">درمووایز را به صفحه اصلی اضافه کنید</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Benefits */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-purple-600">✨</span>
              مزایای نصب اپ
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                دسترسی سریع از صفحه اصلی
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                عملکرد بهتر و سریع‌تر
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                اعلان‌های مفید
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                تجربه شبیه اپلیکیشن موبایل
              </li>
            </ul>
          </div>

          {/* Instructions */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              {isIOS ? <AppleLogo size={20} className="text-gray-600" /> : 
               isAndroid ? <AndroidLogo size={20} className="text-green-600" /> : 
               <DeviceMobile size={20} className="text-blue-600" />}
              {instructions.title}
            </h3>
            
            <div className="space-y-4">
              {instructions.steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <IconComponent size={16} className="text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">{step.text}</p>
                    </div>
                    <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Device Info */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-blue-800 mb-2">اطلاعات دستگاه شما:</h4>
            <p className="text-sm text-blue-700">
              {isIOS ? 'آیفون' : isAndroid ? 'اندروید' : 'دسکتاپ'} - 
              {isSafari ? ' Safari' : ' Chrome'}
            </p>
          </div>

          {/* Footer */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
            >
              بعداً
            </button>
            <button
              onClick={() => {
                // Trigger install prompt if available
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                }
                onClose();
              }}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105"
            >
              نصب کن
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallModal;
